import React, { useCallback, useMemo, useState } from 'react'

import i18n from 'i18n-literally'
import PropTypes from 'prop-types'
import { pick } from 'ramda'
import { useConnect } from 'redux-bundler-hook'

import { Box, CircularProgress, MenuItem } from '@mui/material'
import { makeStyles } from '@mui/styles'

import { Field, Form, Formik } from 'formik'
import * as yup from 'yup'

import Dialog from '~/src/Dialog'
import { useAllowed } from '~/src/Flags'
import createLogger from '~/src/Lib/Logging'
import { EMPTY_OBJECT, parseApiErrors } from '~/src/Lib/Utils'
import { shape as membershipShape } from '~/src/Staff/Membership/bundle/shape'
import { cleanPhoneNumber, validatePhoneNumber } from '~/src/Store/bundles/me/shape'
import Button from '~/src/UI/Shared/Button'
import ConfirmationDialog from '~/src/UI/Shared/ConfirmationDialog'
import PhoneField from '~/src/UI/Shared/Form/PhoneField'
import { FormikTextField } from '~/src/UI/Shared/Form/TextField'
import T from '~/src/UI/Shared/Typography'

const displayName = 'Membership/Form'
const logger = createLogger(displayName)

const useStyles = makeStyles(theme => ({
  content: {
    '& > [class*=MuiFormControl-root]': {
      minHeight: theme.spacing(10),
    },
  },
}))

const validationSchema = yup.object({
  email: yup.string().email(i18n`Please enter a valid email address`).required(i18n`Please enter an email address`),
  firstName: yup.string().trim().required(i18n`Please enter a first name`),
  lastName: yup.string().trim().required(i18n`Please enter a last name`),
  phoneNumber: yup.string()
    .test('is-valid-phone-number', i18n`Please enter a valid phone number`, val => (val ? validatePhoneNumber(val) : true)),
})

const MembershipForm = ({
  loading = false,
  membership = EMPTY_OBJECT,
  onClose,
}) => {
  const {
    facilityRoles,
    me,
    myCurrentMembershipId,
    users,
    doMarkMembershipListAsOutdated,
    doMembershipDelete,
    doMembershipSave,
    doUpdateUrl,
  } = useConnect(
    'selectFacilityRoles',
    'selectMe',
    'selectMyCurrentMembershipId',
    'selectUsers',
    'doMarkMembershipListAsOutdated',
    'doMembershipDelete',
    'doMembershipSave',
    'doUpdateUrl',
  )
  const [isGoTier] = useAllowed({ tiers: ['GO'] })

  const availableFacilityRoleList = Object.values(facilityRoles)
    .filter(facilityRole => (isGoTier ? facilityRole.key === 'GO_ADMINISTRATOR' : true))

  const initialValues = useMemo(() => {
    const {
      [membership.user]: user = {
        firstName: '',
        lastName: '',
        email: '',
        phoneNumber: '',
        languagePref: '',
        registeredPhones: [],
      }
    } = users

    const registeredNumber = user.registeredPhones.find(phone => phone.default && phone.isMobile)

    const adminRoleId = availableFacilityRoleList.find(facilityRole => facilityRole.key === 'GO_ADMINISTRATOR')?.id
    const userRoleId = availableFacilityRoleList.find(facilityRole => facilityRole.key === 'USER')?.id
    const defaultRoleId = isGoTier ? adminRoleId : userRoleId

    return {
      ...user,
      ...pick(['id', 'role', 'admin'], membership),
      phoneNumber: registeredNumber ? cleanPhoneNumber(registeredNumber.number) : '',
      role: membership.role ?? defaultRoleId ?? availableFacilityRoleList[0]?.id,
    }
  }, [availableFacilityRoleList, isGoTier, membership, users])

  const [removalConfirmationOpen, setRemovalConfirmationOpen] = useState(false)
  const handleCloseRemovalConfirmation = useCallback(() => setRemovalConfirmationOpen(false), [])

  const handleConfirmRemoval = useCallback(async () => {
    const result = await doMembershipDelete(membership)
    if (result) {
      setRemovalConfirmationOpen(false)
      doUpdateUrl('/setup/staff')
    }
  }, [doMembershipDelete, doUpdateUrl, membership])

  const handleSubmit = useCallback(async values => {
    logger.debug('submitting:', values)
    const result = await doMembershipSave(values)
    if (result) {
      doMarkMembershipListAsOutdated()
      onClose()
    }
  }, [doMarkMembershipListAsOutdated, doMembershipSave, onClose])

  const isSaving = membership.inflight === 'save'

  const errors = membership.lastError?.error

  const classes = useStyles()

  return (
    <Formik
      initialValues={initialValues}
      validateOnBlur
      validateOnChange
      validationSchema={validationSchema}
      onSubmit={handleSubmit}
    >
      {({ dirty, isSubmitting, isValid, values, setFieldValue, submitForm }) => (
        <>
          <Form>
            <Dialog
              open
              splitActions
              data-testid="account-form"
              title={(
                <T.H2>{membership.id ? i18n`Edit user` : i18n`Add user`}</T.H2>
              )}
              content={(
                loading ? (
                  <Box margin={20} textAlign="center">
                    <CircularProgress color="primary" />
                  </Box>
                ) : (
                  <div className={classes.content}>
                    {errors ? (
                      <Box marginBottom="1rem">
                        <T className="text-danger">{parseApiErrors(errors)}</T>
                      </Box>
                    ) : null}

                    <Field
                      autoFocus={!values.firstName}
                      fullWidth
                      required
                      name="firstName"
                      label={i18n`First name`}
                      maxLength={30}
                      component={FormikTextField}
                    />

                    <Field
                      fullWidth
                      required
                      name="lastName"
                      label={i18n`Last name`}
                      maxLength={30}
                      component={FormikTextField}
                    />

                    {membership.id !== myCurrentMembershipId ? (
                      <Field
                        disabled={availableFacilityRoleList.length === 1}
                        fullWidth
                        select
                        name="role"
                        label={i18n`User role`}
                        component={FormikTextField}
                      >
                        {availableFacilityRoleList.map(facilityRole => (
                          <MenuItem key={facilityRole.id} value={facilityRole.id}>
                            {facilityRole.name}
                          </MenuItem>
                        ))}
                      </Field>
                    ) : null}

                    {membership.id == null ? (
                      <Field
                        fullWidth
                        required
                        type="email"
                        name="email"
                        label={i18n`Email address`}
                        maxLength={254}
                        component={FormikTextField}
                      />
                    ) : (
                      <PhoneField
                        autoFocus={values.firstName && values.lastName && !values.phoneNumber}
                        name="phoneNumber"
                        label={i18n`Contact phone number`}
                        value={values.phoneNumber}
                        onChange={({ phoneNumber }) => {
                          setFieldValue('phoneNumber', cleanPhoneNumber(phoneNumber))
                        }}
                      />
                    )}
                  </div>
                )
              )}
              actions={(
                <>
                  <Box marginRight="auto">
                    {membership.id && !me.memberships.some(m => m.id === membership.id) ? (
                      <Button
                        danger
                        variant="text"
                        onClick={() => setRemovalConfirmationOpen(true)}
                      >
                        {i18n`Remove`}
                      </Button>
                    ) : null}
                  </Box>

                  <Button
                    variant="text"
                    onClick={onClose}
                  >
                    {i18n`Cancel`}
                  </Button>

                  <Button
                    disabled={!dirty || isSubmitting || !isValid || isSaving || loading}
                    onClick={submitForm}
                  >
                    {i18n`Save`}
                  </Button>
                </>
              )}
              onClose={onClose}
            />
          </Form>
          <ConfirmationDialog
            dataTestId="account-delete-confirm"
            open={removalConfirmationOpen}
            title={i18n`Are you sure you want to remove ${values.firstName} ${values.lastName}?`}
            cancelText={i18n`No, keep user.`}
            confirmText={i18n`Yes, delete user.`}
            onCancel={handleCloseRemovalConfirmation}
            onConfirm={handleConfirmRemoval}
          />
        </>
      )}
    </Formik>
  )
}
MembershipForm.propTypes = {
  loading: PropTypes.bool,
  membership: membershipShape,
  onClose: PropTypes.func.isRequired,
}

export default MembershipForm
